import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { useGetPayInfoApi } from '@/api/payment';
import { useTokenStore } from '@/store/tokenStore';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import PaymentModal from '@/components/common/Modal/PaymentModal/PaymentModal';
import { PAYMENT_GOODS_ANNUAL_FEE, PAYMENT_MEMBER_REGULAR, PAYMENT_MEMBER_STUDENT, PAYMENT_MEMBER_TYPES } from '@/constants/payment';
import useIsMobile from '@/hooks/useIsMobile';
import { convertPaymentValue } from '@/utils/convertValue';
import { downloadPdf } from '@/utils/fileUtils';

import * as S from './style';

const MembershipPayment = () => {
	const { t } = useTranslation();
	const isMobile = useIsMobile(900);
	const today = dayjs().format('YYYY');
	const [isOpen, setIsOpen] = useState(false);

	const { isLogin } = useTokenStore();
	const { data: payInfo } = useGetPayInfoApi(isLogin());
	const memberType =
		payInfo?.member_type && PAYMENT_MEMBER_TYPES.includes(payInfo.member_type as (typeof PAYMENT_MEMBER_TYPES)[number]) ? payInfo.member_type : '';
	const formatAnnualFeeDisplay = (amount?: number, type?: string) => {
		const selectedType = type && PAYMENT_MEMBER_TYPES.includes(type as (typeof PAYMENT_MEMBER_TYPES)[number]) ? type : PAYMENT_MEMBER_REGULAR;
		const displayAmount = amount ? amount.toLocaleString() : convertPaymentValue(selectedType);
		const displayType = selectedType ? ` (${t(selectedType)})` : '';

		return `₩ ${displayAmount}${displayType}`;
	};

	const onClickPayment = () => {
		setIsOpen(true);
	};

	return (
		<S.Container>
			<S.Title id="membership-payment-title">
				{t('membership.payment.title')}
				<S.PaymentButton disabled={!payInfo?.pay_possible} onClick={onClickPayment}>
					{t('membership.payment.payButton')}
				</S.PaymentButton>
			</S.Title>
			<>
				{!isMobile && (
					<S.Table>
						<S.TableHead>
							<S.TableRow>
								<S.TableHeadCell>{t('common.date.year')}</S.TableHeadCell>
								<S.TableHeadCell>{t('common.annualFee')}</S.TableHeadCell>
								<S.TableHeadCell>{t('membership.payment.paymentMethod')}</S.TableHeadCell>
								<S.TableHeadCell>{t('membership.payment.paymentDate')}</S.TableHeadCell>
								<S.TableHeadCell>{t('membership.payment.paymentStatus')}</S.TableHeadCell>
							</S.TableRow>
						</S.TableHead>
						<S.TableBody>
							{payInfo &&
								payInfo.pay_info.map((info, index) => (
									<S.TableRow key={index}>
										<S.TableCell>{info.pay_info_pk ? dayjs(info.pay_info_tran_date).format('YYYY') : today}</S.TableCell>
										<S.PointCel>{info.pay_info_pk ? formatAnnualFeeDisplay(info.pay_info_amt, info.pay_info_member_type) : '-'}</S.PointCel>
										<S.TableCell>{info.pay_info_pay_method ? info.pay_info_pay_method : '-'}</S.TableCell>
										<S.TableCell>{info.pay_info_pk ? dayjs(info.pay_info_enrollment_date).format('YYYY-MM-DD') : '-'}</S.TableCell>
										<S.TableCell>
											{info.pay_info_pk ? (
												<S.ButtonBox>
													<S.TableCellSuceess>{info.pay_info_state}</S.TableCellSuceess>
													{info.pay_info_pay_method !== '무통장입금' && (
														<S.CompleteStatus type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'receipt')}>
															{t('membership.payment.receipt')}
														</S.CompleteStatus>
													)}
													{info.pay_info_state === '결제완료' && (
														<S.CompleteStatus type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'transaction')}>
															{t('membership.payment.transaction')}
														</S.CompleteStatus>
													)}
												</S.ButtonBox>
											) : (
												<S.TableCellDanger>{t('membership.payment.unpaid')}</S.TableCellDanger>
											)}
										</S.TableCell>
									</S.TableRow>
								))}
						</S.TableBody>
					</S.Table>
				)}
				{isMobile &&
					payInfo &&
					payInfo.pay_info.map((info, index) => (
						<S.MobileCard key={index}>
							<S.CardContentBox>
								<S.MenuBox>
									<S.CardMenu>{t('common.date.year')}</S.CardMenu>
									<div>{info.pay_info_pk ? dayjs(info.pay_info_tran_date).format('YYYY') : today}</div>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>{t('common.annualFee')}</S.CardMenu>
									<S.PointMenu>
										{info.pay_info_pk
											? formatAnnualFeeDisplay(info.pay_info_amt, info.pay_info_member_type)
											: '-'}
									</S.PointMenu>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>{t('membership.payment.paymentMethod')}</S.CardMenu>
									<div>{info.pay_info_pay_method ? info.pay_info_pay_method : '-'}</div>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>{t('membership.payment.paymentDate')}</S.CardMenu>
									<div>
										{info.pay_info_pk ? dayjs(info.pay_info_enrollment_date).format('YYYY-MM-DD') : '-'}
									</div>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>{t('membership.payment.paymentStatus')}</S.CardMenu>
									<div>
										{info.pay_info_pk ? (
											<S.TableCellSuceess>{info.pay_info_state}</S.TableCellSuceess>
										) : (
											<S.TableCellDanger>{t('membership.payment.unpaid')}</S.TableCellDanger>
										)}
									</div>
								</S.MenuBox>
							</S.CardContentBox>
							{info.pay_info_pk ? (
								<S.ButtonBox>
									{info.pay_info_pay_method !== '무통장입금' && (
										<S.Button type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'receipt')}>
											{t('membership.payment.receipt')}
										</S.Button>
									)}
									{info.pay_info_state === '결제완료' && (
										<S.Button type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'transaction')}>
											{t('membership.payment.transaction')}
										</S.Button>
									)}
								</S.ButtonBox>
							) : null}
						</S.MobileCard>
					))}

				{isOpen && payInfo && (
					<ModalPortal>
						<ModalBox onClick={() => setIsOpen(false)}>
							<PaymentModal closeModal={setIsOpen} goodsName={PAYMENT_GOODS_ANNUAL_FEE} memberType={memberType} />
						</ModalBox>
					</ModalPortal>
				)}
			</>
		</S.Container>
	);
};
export default MembershipPayment;
